import { buildUrl, http, putFile, request, type Query } from './client';
import type { L10n } from '../i18n';
import type * as T from './types';

export * from './client';
export type * from './types';

/** Деякі списки за контрактом — масиви, інші — сторінки. Нормалізуємо обидва варіанти. */
export function asPage<X>(r: X[] | T.Page<X> | null | undefined): T.Page<X> {
  if (!r) return { items: [], next_cursor: null };
  if (Array.isArray(r)) return { items: r, next_cursor: null };
  return { items: r.items ?? [], next_cursor: r.next_cursor ?? null };
}
export function asArray<X>(r: X[] | T.Page<X> | null | undefined): X[] {
  return asPage(r).items;
}

type Paged = { limit?: number; cursor?: string | null };

export const authApi = {
  login: (body: T.LoginIn) => http.post<T.LoginOut>('/auth/login', body),
  logout: () => http.post<{ ok: boolean }>('/auth/logout'),
  me: () => http.get<T.Me>('/auth/me'),
  totpSetup: () => http.post<{ secret: string; otpauth_uri: string }>('/auth/totp/setup'),
  totpEnable: (secret: string, code: string) => http.post<{ ok: boolean }>('/auth/totp/enable', { secret, code }),
};

export const meApi = {
  putPrefs: (body: { country?: string | null; workspace?: T.WorkspaceKey }) =>
    http.put<T.Scope & { workspace?: T.WorkspaceKey | null }>('/me/prefs', body),
};

export const workspaceApi = {
  today: () => http.get<T.Today>('/today'),
  search: (q: string, signal?: AbortSignal) => http.get<{ items: T.SearchItem[] }>('/search', { q }, signal),
};

export const metaApi = {
  get: () => http.get<T.Meta>('/meta'),
};

export const leadsApi = {
  list: (f: T.LeadFilter & Paged) => http.get<T.Page<T.Lead>>('/leads', f as Query).then(asPage),
  get: (id: string) => http.get<T.Lead>(`/leads/${id}`),
  patch: (id: string, body: T.LeadPatch) => http.patch<T.Lead>(`/leads/${id}`, body),
  create: (body: T.LeadCreate) => http.post<T.Lead | { id: string }>('/leads', body),
  convert: (id: string, body: T.LeadConvert) => http.post<{ case_id: string }>(`/leads/${id}/convert`, body),
  history: (id: string) => http.get<{ lead: T.Lead; items: T.LeadHistoryItem[] }>(`/leads/${id}/history`),
};

export const dutyApi = {
  list: (from?: string, to?: string) =>
    http.get<T.DutyShift[] | T.Page<T.DutyShift>>('/duty-shifts', { from, to }).then(asArray),
  create: (body: T.DutyShiftCreate) => http.post<T.DutyShift>('/duty-shifts', body),
  remove: (id: string) => http.del<unknown>(`/duty-shifts/${id}`),
  /** Сервер повертає одну зміну або null; підтримуємо й масив. */
  current: () =>
    http
      .get<T.DutyShift | T.DutyShift[] | T.Page<T.DutyShift> | null>('/duty-shifts/current')
      .then((r) => (r == null ? [] : Array.isArray(r) || 'items' in r ? asArray(r as T.DutyShift[]) : [r as T.DutyShift])),
};

export const inboxApi = {
  list: (f: { status?: string; channel?: string; assigned_to?: string } & Paged) =>
    http.get<T.Page<T.Conversation>>('/conversations', f as Query).then(asPage),
  messages: (id: string, p: Paged = {}) =>
    http.get<T.Page<T.Message>>(`/conversations/${id}/messages`, p as Query).then(asPage),
  send: (id: string, body: string) => http.post<T.Message>(`/conversations/${id}/messages`, { body }),
  patch: (id: string, body: T.ConversationPatch) => http.patch<T.Conversation>(`/conversations/${id}`, body),
  read: (id: string) => http.post<unknown>(`/conversations/${id}/read`),
};

export const casesApi = {
  list: (f: { service?: string; state?: string; assignee?: string; q?: string; open?: 1 } & Paged) =>
    http.get<T.Page<T.CaseSummary>>('/cases', f as Query).then(asPage),
  board: (service: string) => http.get<T.CaseBoard>('/cases/board', { service }),
  create: (body: T.CaseCreate) =>
    http.post<{ id: string; customer_match?: T.CustomerMatch } & Partial<T.CaseDetail>>('/cases', body),
  get: (id: string) => http.get<T.CaseDetail>(`/cases/${id}`),
  patch: (id: string, body: T.CasePatch) => http.patch<T.CaseDetail>(`/cases/${id}`, body),
  transition: (id: string, to: string, note?: string) =>
    http.post<T.CaseEvent>(`/cases/${id}/transition`, { to, note: note || undefined }),
  events: (id: string) => http.get<T.CaseEvent[] | T.Page<T.CaseEvent>>(`/cases/${id}/events`).then(asArray),
  comments: (id: string) => http.get<T.Comment[] | T.Page<T.Comment>>(`/cases/${id}/comments`).then(asArray),
  addComment: (id: string, body: string, mentions?: string[]) =>
    http.post<T.Comment>(`/cases/${id}/comments`, { body, mentions }),
  tasks: (id: string) => http.get<T.Task[] | T.Page<T.Task>>(`/cases/${id}/tasks`).then(asArray),
  addTask: (id: string, body: { title: string; assignee_id?: string; due_at?: string }) =>
    http.post<T.Task>(`/cases/${id}/tasks`, body),
  patchTask: (taskId: string, body: { done?: boolean; title?: string; due_at?: string | null; assignee_id?: string | null }) =>
    http.patch<T.Task>(`/tasks/${taskId}`, body),
  myTasks: () => http.get<T.Task[] | T.Page<T.Task>>('/tasks', { mine: 1, open: 1 }).then(asArray),
  allTasks: (f: { mine?: boolean; open?: boolean }) =>
    http.get<T.Task[] | T.Page<T.Task>>('/tasks', { mine: f.mine ? 1 : undefined, open: f.open ? 1 : undefined, limit: 500 }).then(asArray),
  setTags: (id: string, tags: string[]) => http.put<unknown>(`/cases/${id}/tags`, { tags }),
  addContact: (id: string, body: { name?: string; phone?: string; email?: string; role: T.CaseContact['role'] }) =>
    http.post<T.CaseContact>(`/cases/${id}/contacts`, body),
  putDeceased: (id: string, body: T.Deceased) => http.put<T.Deceased>(`/cases/${id}/deceased`, body),
  medical: (id: string) => http.get<T.Medical>(`/cases/${id}/medical`),
  putMedical: (id: string, body: T.Medical) => http.put<T.Medical>(`/cases/${id}/medical`, body),
  invite: (id: string, contact_id: string, channel: 'sms' | 'email') =>
    http.post<unknown>(`/cases/${id}/invite`, { contact_id, channel }),
  // batch: B-case-flow
  /** Список справ з фільтром робочого простору (?workspace=) і «лише мої» (?mine=1). */
  listInWorkspace: (f: { workspace?: T.CaseWorkspaceKey; mine?: 1; service?: string; state?: string; q?: string } & Paged) =>
    http.get<T.Page<T.CaseSummary>>('/cases', f as Query).then(asPage),
  moveWorkspace: (id: string, body: T.CaseWorkspaceMoveIn) => http.post<T.CaseDetail>(`/cases/${id}/workspace`, body),
  workspaceHistory: (id: string) => http.get<T.WorkspaceMove[]>(`/cases/${id}/workspace-history`).then(asArray),
};

export const documentsApi = {
  checklist: (caseId: string) => http.get<T.ChecklistItem[]>(`/cases/${caseId}/checklist`).then(asArray),
  setChecklist: (itemId: string, status: T.ChecklistStatus, note?: string, opts?: { confirmGenerated?: boolean }) =>
    http.patch<T.ChecklistItem>(`/checklist/${itemId}`, { status, note, ...(opts?.confirmGenerated ? { confirm_generated: true } : {}) }),
  list: (caseId: string) => http.get<T.DocumentRow[] | T.Page<T.DocumentRow>>(`/cases/${caseId}/documents`).then(asArray),
  uploadUrl: (caseId: string, body: { document_type_key?: string; file_name: string; mime: string; size: number }) =>
    http.post<T.UploadUrlOut>(`/cases/${caseId}/documents/upload-url`, body),
  complete: (docId: string) => http.post<T.DocumentRow>(`/documents/${docId}/complete`),
  downloadUrl: (docId: string, inline = false) =>
    http.get<{ url: string; mime: string; file_name: string; inline: boolean }>(`/documents/${docId}/download-url`, { inline: inline || undefined }),
  remove: (docId: string) => http.del<unknown>(`/documents/${docId}`),
  generate: (caseId: string, template: 'contract' | 'invoice' | 'act') =>
    http.post<T.DocumentRow>(`/cases/${caseId}/documents/generate`, { template }),
};

/**
 * Presigned-флоу: upload-url → PUT файлу напряму в сховище → complete.
 * Повертає id документа.
 */
export async function uploadCaseDocument(caseId: string, file: File, documentTypeKey?: string): Promise<string> {
  const mime = file.type || 'application/octet-stream';
  const target = await documentsApi.uploadUrl(caseId, {
    document_type_key: documentTypeKey,
    file_name: file.name,
    mime,
    size: file.size,
  });
  await putFile(target.upload_url, file, target.headers ?? { 'Content-Type': mime }, target.method || 'PUT');
  await documentsApi.complete(target.document_id);
  return target.document_id;
}

export const catalogApi = {
  list: (f: { status?: string; category?: string; q?: string } & Paged) =>
    http.get<T.Page<T.ProductSummary>>('/products', f as Query).then(asPage),
  get: (id: string) => http.get<T.Product>(`/products/${id}`),
  create: (body: T.ProductInput) => http.post<T.Product>('/products', body),
  patch: (id: string, body: Partial<T.ProductInput>) => http.patch<T.Product>(`/products/${id}`, body),
  remove: (id: string) => http.del<unknown>(`/products/${id}`),
  bulk: (ids: string[], status: T.ProductStatus) => http.post<unknown>('/products/bulk', { ids, status }),
  imageUploadUrl: (file_name: string, mime: string) =>
    http.post<{ upload_url: string; public_url: string }>('/uploads/image', { file_name, mime }),
  categories: () => http.get<T.Category[] | T.Page<T.Category>>('/categories').then(asArray),
  createCategory: (body: { key: string; name: Record<string, string>; parent_id?: string }) =>
    http.post<T.Category>('/categories', body),
};

export const dictApi = {
  defs: (includeArchived = false) => http.get<T.AttributeDef[]>('/attribute-defs', { include_archived: includeArchived || undefined }),
  createDef: (body: T.AttributeDefInput) => http.post<T.AttributeDef>('/attribute-defs', body),
  patchDef: (id: string, body: Partial<Omit<T.AttributeDefInput, 'key' | 'type'>> & { archived?: boolean }) =>
    http.patch<T.AttributeDef>(`/attribute-defs/${id}`, body),
  addOption: (defId: string, body: { name: Record<string, string>; swatch?: string | null; key?: string }) =>
    http.post<T.AttributeOption>(`/attribute-defs/${defId}/options`, body),
  patchOption: (id: string, body: { name?: Record<string, string>; swatch?: string | null; position?: number; archived?: boolean }) =>
    http.patch<T.AttributeOption>(`/attribute-options/${id}`, body),
};

export const demandApi = {
  overview: (f: { category?: string; status?: string; q?: string; opt?: string[]; collection_id?: string }) =>
    http.get<T.CatalogOverview>('/catalog/overview', f as Query),
  rebuild: (days: number) => http.post<{ rows: number }>('/catalog/stats/rebuild', { days }),
  product: (id: string) =>
    http.get<{
      item: T.CatalogItem;
      by_channel: { channel_id: string | null; name: string | null; kind: string | null; units: number; revenue_minor: number; margin_minor: number | null }[];
      by_variant: { variant_id: string; units: number }[];
      incoming: number;
    }>(`/products/${id}/demand`),
  generateVariants: (productId: string, skuPrefix?: string) =>
    http.post<{ created: number; deactivated: number; total: number; product: T.Product }>(`/products/${productId}/variants/generate`, {
      sku_prefix: skuPrefix || undefined,
    }),
};

export const collectionsApi = {
  list: (status?: T.CollectionStatus) => http.get<T.Collection[]>('/collections', { status }),
  get: (id: string) => http.get<T.Collection>(`/collections/${id}`),
  create: (body: T.CollectionInput) => http.post<T.Collection>('/collections', body),
  patch: (id: string, body: Partial<Omit<T.CollectionInput, 'product_ids'>>) => http.patch<T.Collection>(`/collections/${id}`, body),
  putItems: (id: string, items: { product_id: string; price_minor?: number | null }[]) => http.put<T.Collection>(`/collections/${id}/items`, items),
};

export const fleetApi = {
  cargoTypes: (includeArchived = false) => http.get<T.CargoType[]>('/cargo-types', { include_archived: includeArchived || undefined }),
  createCargoType: (body: { key: string; name: Record<string, string>; stackable?: boolean; uses_floor?: boolean }) =>
    http.post<T.CargoType>('/cargo-types', body),
  patchCargoType: (id: string, body: { name?: Record<string, string>; stackable?: boolean; uses_floor?: boolean; archived?: boolean }) =>
    http.patch<T.CargoType>(`/cargo-types/${id}`, body),
  vehicleTypes: () => http.get<T.VehicleType[]>('/vehicle-types'),
  createVehicleType: (body: Partial<T.VehicleTypeInput> & { name: string; body_type: T.BodyType }) => http.post<T.VehicleType>('/vehicle-types', body),
  patchVehicleType: (id: string, body: Partial<T.VehicleTypeInput> & { archived?: boolean }) => http.patch<T.VehicleType>(`/vehicle-types/${id}`, body),
  carriers: () => http.get<T.Carrier[]>('/carriers'),
  createCarrier: (body: { name: string; country: string }) => http.post<T.Carrier>('/carriers', body),
  vehicles: (f: { ownership?: T.Ownership; owner_org_id?: string; q?: string; date_from?: string; date_to?: string } = {}) =>
    http.get<T.Vehicle[]>('/vehicles', f as Query),
  vehicle: (id: string) => http.get<T.Vehicle>(`/vehicles/${id}`),
  createVehicle: (body: T.VehicleInput) => http.post<T.Vehicle>('/vehicles', body),
  patchVehicle: (id: string, body: Partial<T.VehicleInput> & { status?: T.Vehicle['status'] }) => http.patch<T.Vehicle>(`/vehicles/${id}`, body),
  trips: (f: { vehicle_id?: string; status?: T.TripStatus; driver?: string; date_from?: string; date_to?: string } = {}) =>
    http.get<T.TripSummary[]>('/trips', f as Query),
  trip: (id: string) => http.get<T.Trip>(`/trips/${id}`),
  createTrip: (body: { vehicle_id: string; driver_id?: string | null; notes?: string | null; stops: T.StopInput[] }) => http.post<T.Trip>('/trips', body),
  patchTrip: (id: string, body: { driver_id?: string | null; status?: T.TripStatus; notes?: string | null }) => http.patch<T.Trip>(`/trips/${id}`, body),
  patchStop: (id: string, body: Partial<T.StopInput> & { eta_fact?: string | null; etd_fact?: string | null }) => http.patch<T.Trip>(`/trip-stops/${id}`, body),
  book: (
    tripId: string,
    body: { shipment_id?: string; shipment?: T.ShipmentInput; cargo_type?: string; qty?: number; from_stop_id: string; to_stop_id: string; positions?: number[] },
  ) => http.post<{ load_id: string; shipment_id: string; trip: T.Trip }>(`/trips/${tripId}/loads`, body),
  unbook: (loadId: string) => http.del<T.Trip>(`/trip-loads/${loadId}`),
  search: (f: { cargo: string; route_from?: string; route_to?: string; on?: string; days?: number; qty?: number }) =>
    http.get<{ trips: T.CapacityHit[]; offers: T.CapacityOffer[] }>('/capacity/search', f as Query),
  offers: () => http.get<T.CapacityOffer[]>('/capacity-offers'),
  createOffer: (body: Omit<T.CapacityOffer, 'id' | 'carrier_name' | 'plate' | 'source' | 'updated_at' | 'trip_id'> & { trip_id?: string | null }) =>
    http.post<T.CapacityOffer>('/capacity-offers', body),
  deleteOffer: (id: string) => http.del<unknown>(`/capacity-offers/${id}`),
};

export const pipelineApi = {
  blockTypes: (includeArchived = false) => http.get<T.BlockType[]>('/block-types', { include_archived: includeArchived || undefined }),
  createBlockType: (body: { key: string; name: Record<string, string>; kind: T.BlockKind; nuance?: Record<string, string>; line_spec?: T.LineSpec[] }) =>
    http.post<T.BlockType>('/block-types', body),
  patchBlockType: (id: string, body: { name?: Record<string, string>; kind?: T.BlockKind; nuance?: Record<string, string>; line_spec?: T.LineSpec[]; archived?: boolean }) =>
    http.patch<T.BlockType>(`/block-types/${id}`, body),
  addRequirement: (blockTypeId: string, body: T.RequirementInput) => http.post<T.BlockRequirement>(`/block-types/${blockTypeId}/requirements`, body),
  patchRequirement: (id: string, body: T.RequirementInput) => http.patch<T.BlockRequirement>(`/block-requirements/${id}`, body),
  deleteRequirement: (id: string) => http.del<unknown>(`/block-requirements/${id}`),
  templates: () => http.get<T.PipelineTemplate[]>('/pipeline-templates'),
  createTemplate: (body: { name: string; service_type_key?: string | null; match_origin?: string | null; match_dest?: string | null; toggles?: T.TemplateToggle[] }) =>
    http.post<T.PipelineTemplate>('/pipeline-templates', body),
  patchTemplate: (id: string, body: { name?: string; match_origin?: string | null; match_dest?: string | null; toggles?: T.TemplateToggle[]; is_active?: boolean }) =>
    http.patch<T.PipelineTemplate>(`/pipeline-templates/${id}`, body),
  putTemplateBlocks: (id: string, blocks: { block_type_id: string; optional?: boolean; toggle_key?: string | null; toggle_when?: boolean }[]) =>
    http.put<T.PipelineTemplate>(`/pipeline-templates/${id}/blocks`, blocks),
  preview: (f: { template_id: string; origin?: string; dest?: string; transit?: string; mode?: string; flags?: string }) =>
    http.get<{ flags: Record<string, boolean>; blocks: { block_type_id: string; name: L10n; requirements: { id: string; kind: T.ReqKind; title: string; required: boolean; specific: boolean }[] }[] }>(
      '/pipeline-preview',
      f as Query,
    ),
  get: (caseId: string) => http.get<T.Pipeline>(`/cases/${caseId}/pipeline`),
  apply: (caseId: string, body: { template_id: string; flags?: Record<string, boolean>; transport_mode?: string; transit_countries?: string[] }) =>
    http.post<T.Pipeline>(`/cases/${caseId}/pipeline`, body),
  settings: (caseId: string, body: { flags?: Record<string, boolean>; transport_mode?: string; transit_countries?: string[] }) =>
    http.patch<T.Pipeline>(`/cases/${caseId}/pipeline`, body),
  addBlock: (caseId: string, body: { block_type_id: string; position?: number; title?: string }) => http.post<T.Pipeline>(`/cases/${caseId}/blocks`, body),
  patchBlock: (id: string, body: { title?: string | null; nuance?: string | null; cost_minor?: number | null; cost_currency?: string | null; price_minor?: number | null }) =>
    http.patch<T.Pipeline>(`/case-blocks/${id}`, body),
  removeBlock: (id: string) => http.del<T.Pipeline>(`/case-blocks/${id}`),
  addParty: (blockId: string, body: { role: string; name?: string; org_id?: string; contact_id?: string; user_id?: string; note?: string }) =>
    http.post<T.Pipeline>(`/case-blocks/${blockId}/parties`, body),
  removeParty: (id: string) => http.del<T.Pipeline>(`/case-block-parties/${id}`),
  addItem: (blockId: string, body: { title: string; note?: string; required?: boolean }) => http.post<T.Pipeline>(`/case-blocks/${blockId}/items`, body),
  patchItem: (id: string, body: { done?: boolean; title?: string }) => http.patch<T.Pipeline>(`/case-block-items/${id}`, body),
  removeItem: (id: string) => http.del<T.Pipeline>(`/case-block-items/${id}`),
  addProduct: (caseId: string, body: { variant_id: string; qty?: number; case_block_id?: string | null }) =>
    http.post<T.Pipeline>(`/cases/${caseId}/pipeline/products`, body),
  removeLine: (caseId: string, itemId: string) => http.del<T.Pipeline>(`/cases/${caseId}/pipeline/lines/${itemId}`),
};

export const plannerApi = {
  get: (f: { date_from: string; date_to: string; user_id?: string }) => http.get<T.PlannerData>('/planner', f as Query),
  templates: () => http.get<T.ShiftTemplate[]>('/shift-templates'),
  createTemplate: (body: { name: string; start_time: string; end_time: string; weekdays: number[] }) => http.post<T.ShiftTemplate>('/shift-templates', body),
  applyTemplate: (id: string, body: { user_ids: string[]; date_from: string; date_to: string }) =>
    http.post<{ created: number; skipped: number }>(`/shift-templates/${id}/apply`, body),
  createShift: (body: { user_id: string; starts_at: string; ends_at: string; title?: string | null; note?: string | null }) => http.post<unknown>('/work-shifts', body),
  deleteShift: (id: string) => http.del<unknown>(`/work-shifts/${id}`),
  absences: (f: { status?: string; user_id?: string } = {}) => http.get<T.Absence[]>('/absences', f as Query),
  requestAbsence: (body: { kind: T.AbsenceKind; starts_on: string; ends_on: string; note?: string | null; user_id?: string | null }) =>
    http.post<T.Absence>('/absences', body),
  approve: (id: string, note?: string) => http.post<T.Absence>(`/absences/${id}/approve`, { note }),
  reject: (id: string, note?: string) => http.post<T.Absence>(`/absences/${id}/reject`, { note }),
  cancel: (id: string) => http.post<T.Absence>(`/absences/${id}/cancel`),
};

export const archiveApi = {
  list: (f: { folder?: string; owner_kind?: string; owner_id?: string; q?: string; validity?: string }) =>
    http.get<T.ArchiveDoc[]>('/documents', f as Query),
  summary: () => http.get<{ folders: { key: T.DocFolder; count: number }[]; total: number; alerts: T.DocAlert[] }>('/documents/summary'),
  uploadUrl: (body: {
    owner_kind: T.OwnerKind;
    owner_id?: string | null;
    document_type_key: string;
    file_name: string;
    mime: string;
    size: number;
    title?: string | null;
    number?: string | null;
    valid_from?: string | null;
    valid_until?: string | null;
  }) => http.post<T.UploadUrlOut>('/documents/upload-url', body),
  patch: (id: string, body: { title?: string | null; number?: string | null; valid_from?: string | null; valid_until?: string | null }) =>
    http.patch<T.ArchiveDoc>(`/documents/${id}`, body),
};

export const freightApi = {
  orders: (status?: string) => http.get<T.ProductionOrder[]>('/production-orders', { status }),
  createOrder: (body: { supplier_org_id?: string | null; expected_at?: string | null; to_location_id?: string | null; notes?: string | null; lines: { variant_id: string; qty: number; unit_cost_minor?: number | null }[] }) =>
    http.post<T.ProductionOrder>('/production-orders', body),
  patchOrder: (id: string, body: { status?: T.ProductionOrder['status']; expected_at?: string | null; to_location_id?: string | null; notes?: string | null }) =>
    http.patch<T.ProductionOrder>(`/production-orders/${id}`, body),
  trips: (f: { date_from?: string; date_to?: string } = {}) => http.get<{ from: string; to: string; trips: T.FreightTripRow[] }>('/freight/trips', f as Query),
  trip: (id: string) => http.get<T.FreightTrip>(`/freight/trips/${id}`),
  stack: (id: string, body: { production_order_line_id: string; position: number }) => http.post<T.FreightTrip>(`/freight/trips/${id}/stacks`, body),
  unstack: (loadId: string) => http.del<T.FreightTrip>(`/freight/loads/${loadId}`),
};

/** Документ будь-якого власника: upload-url → PUT у сховище → complete. */
export async function uploadOwnerDocument(
  file: File,
  body: Omit<Parameters<typeof archiveApi.uploadUrl>[0], 'file_name' | 'mime' | 'size'>,
): Promise<string> {
  const mime = file.type || 'application/octet-stream';
  const target = await archiveApi.uploadUrl({ ...body, file_name: file.name, mime, size: file.size });
  await putFile(target.upload_url, file, target.headers ?? { 'Content-Type': mime }, target.method || 'PUT');
  await documentsApi.complete(target.document_id);
  return target.document_id;
}

/** Завантаження фото товару в публічний бакет. Повертає публічну URL. */
export async function uploadProductImage(file: File): Promise<string> {
  const mime = file.type || 'image/png';
  const { upload_url, public_url } = await catalogApi.imageUploadUrl(file.name, mime);
  await putFile(upload_url, file, { 'Content-Type': mime });
  return public_url;
}

export const pricingApi = {
  lists: () => http.get<T.PriceList[] | T.Page<T.PriceList>>('/price-lists').then(asArray),
  createList: (body: Omit<T.PriceList, 'id'>) => http.post<T.PriceList>('/price-lists', body),
  items: (id: string) => http.get<T.PriceListItem[] | T.Page<T.PriceListItem>>(`/price-lists/${id}/items`).then(asArray),
  putItems: (id: string, items: Omit<T.PriceListItem, 'id' | 'sku' | 'product_name'>[]) =>
    http.put<unknown>(`/price-lists/${id}/items`, items),
  rules: () => http.get<T.PricingRule[] | T.Page<T.PricingRule>>('/pricing-rules').then(asArray),
  createRule: (body: T.PricingRuleInput) => http.post<T.PricingRule>('/pricing-rules', body),
  patchRule: (id: string, body: Partial<T.PricingRuleInput>) => http.patch<T.PricingRule>(`/pricing-rules/${id}`, body),
  deleteRule: (id: string) => http.del<unknown>(`/pricing-rules/${id}`),
  groups: () => http.get<T.CustomerGroup[] | T.Page<T.CustomerGroup>>('/customer-groups').then(asArray),
  createGroup: (name: string) => http.post<T.CustomerGroup>('/customer-groups', { name }),
  quote: (q: { variant_id: string; qty: number; channel_id?: string; buyer_org_id?: string; currency?: string }, signal?: AbortSignal) =>
    http.get<T.Quote>('/pricing/quote', q as Query, signal),
  /** Підсумкові ціни для кількох варіантів (список пошуку в майстрі замовлення). */
  quotes: (body: { variant_ids: string[]; qty?: number; channel_id?: string; buyer_org_id?: string; currency?: string }) =>
    http.post<{ items: { variant_id: string; unit_price_minor: number; currency: string; list_unit_price_minor: number; collection_id: string | null; rule_id: string | null }[] }>(
      '/pricing/quotes',
      body,
    ),
  fxRates: (date?: string) => http.get<T.FxRate[] | T.Page<T.FxRate>>('/fx/rates', { date }).then(asArray),
  fxRefresh: () => http.post<unknown>('/fx/refresh'),
  fxSettings: () => http.get<T.FxSetting[] | T.Page<T.FxSetting>>('/fx/settings').then(asArray),
  putFxSettings: (rows: T.FxSetting[]) => http.put<unknown>('/fx/settings', rows),
};

export const inventoryApi = {
  locations: () => http.get<T.StockLocation[] | T.Page<T.StockLocation>>('/stock-locations').then(asArray),
  createLocation: (body: { name: string; city?: string; address?: string }) =>
    http.post<T.StockLocation>('/stock-locations', body),
  levels: (f: { location_id?: string; q?: string; low?: boolean }) =>
    http
      .get<T.InventoryLevel[] | T.Page<T.InventoryLevel>>('/inventory', {
        location_id: f.location_id,
        q: f.q,
        low: f.low ? 1 : undefined,
      })
      .then(asArray),
  move: (body: { variant_id: string; location_id: string; kind: T.MovementKind; qty: number; note?: string }) =>
    http.post<T.Movement>('/inventory/movements', body),
  transfer: (body: { variant_id: string; from_location_id: string; to_location_id: string; qty: number }) =>
    http.post<unknown>('/inventory/transfer', body),
  movements: (f: { variant_id?: string } & Paged) =>
    http.get<T.Page<T.Movement>>('/inventory/movements', f as Query).then(asPage),
};

export const ordersApi = {
  list: (f: { status?: string; channel_id?: string; q?: string; from?: string; to?: string } & Paged) =>
    http.get<T.Page<T.OrderSummary>>('/orders', f as Query).then(asPage),
  get: (id: string) => http.get<T.Order>(`/orders/${id}`),
  create: (body: T.OrderCreate) => http.post<T.Order>('/orders', body),
  patch: (id: string, body: { items?: T.OrderItemInput[]; delivery?: T.Delivery; notes?: string }) =>
    http.patch<T.Order>(`/orders/${id}`, body),
  confirm: (id: string) => http.post<T.Order>(`/orders/${id}/confirm`),
  ship: (id: string, tracking?: string) => http.post<T.Order>(`/orders/${id}/ship`, { tracking: tracking || undefined }),
  complete: (id: string) => http.post<T.Order>(`/orders/${id}/complete`),
  cancel: (id: string) => http.post<T.Order>(`/orders/${id}/cancel`),
  invoiceUrl: (id: string) => buildUrl(`/orders/${id}/invoice.pdf`),
  /** PDF через fetch (з Bearer, якщо cookie недоступна) → blob URL. */
  summaryBlob: async (caseId: string) => {
    const res = await request<Response>('GET', `/cases/${caseId}/summary.pdf`, { raw: true, headers: { Accept: 'application/pdf' } });
    return URL.createObjectURL(await res.blob());
  },
  /** PDF рахунку; `sellerMissing` — які реквізити продавця не заповнені (рахунок друкується, CRM нагадує). */
  invoiceBlob: async (id: string) => {
    const res = await request<Response>('GET', `/orders/${id}/invoice.pdf`, { raw: true, headers: { Accept: 'application/pdf' } });
    const missing = res.headers.get('X-Seller-Missing');
    return { url: URL.createObjectURL(await res.blob()), sellerMissing: missing ? missing.split(',') : [] };
  },
};

export const b2bApi = {
  list: () => http.get<T.B2BAccount[] | T.Page<T.B2BAccount>>('/b2b/accounts').then(asArray),
  create: (body: T.B2BAccountInput) => http.post<T.B2BAccount>('/b2b/accounts', body),
  patch: (id: string, body: Partial<T.B2BAccountInput>) => http.patch<T.B2BAccount>(`/b2b/accounts/${id}`, body),
  orders: (id: string) => http.get<T.OrderSummary[] | T.Page<T.OrderSummary>>(`/b2b/accounts/${id}/orders`).then(asArray),
  balance: (id: string) => http.get<T.B2BBalance>(`/b2b/accounts/${id}/balance`),
};

export const channelsApi = {
  list: () => http.get<T.SalesChannel[] | T.Page<T.SalesChannel>>('/sales-channels').then(asArray),
  patch: (id: string, body: { is_active?: boolean; price_list_id?: string | null; stock_buffer?: number; config?: Record<string, unknown> }) =>
    http.patch<T.SalesChannel>(`/sales-channels/${id}`, body),
  rotate: (id: string) => http.post<T.SalesChannel & { feed_token?: string }>(`/sales-channels/${id}/rotate-feed-token`),
  feedUrl: (token: string) => buildUrl(`/feeds/prom/${token}.xml`),
};

export const dashboardApi = {
  get: () => http.get<T.Dashboard>('/dashboard'),
  /** Ряди для графіка «Усі продажі»: qty — усі продані одиниці (не лише цільові), як і revenue_minor. */
  sales: async (from: string, to: string, group: 'day' | 'week' = 'day'): Promise<T.SalesPoint[]> => {
    const r = await http.get<T.SalesResponse | T.SalesRow[]>('/dashboard/sales', { from, to, group });
    const rows: T.SalesRow[] = Array.isArray(r) ? r : (r.series ?? r.items ?? []);
    return rows.map((x) => ({
      date: x.bucket ?? x.date ?? '',
      qty: x.qty ?? 0,
      revenue_minor: x.revenue_minor ?? 0,
      orders: x.orders,
    }));
  },
};

export const adminApi = {
  users: () => http.get<T.User[] | T.Page<T.User>>('/users').then(asArray),
  createUser: (body: T.UserCreate) => http.post<T.User>('/users', body),
  patchMembership: (userId: string, body: { role?: T.StaffRole; status?: 'active' | 'suspended'; countries?: string[] }) =>
    http.patch<unknown>(`/memberships/${userId}`, body),
  site: (id: string) => http.get<T.Site>(`/sites/${id}`),
  patchSite: (id: string, body: Partial<T.Site>) => http.patch<T.Site>(`/sites/${id}`, body),
  audit: (f: { table?: string; row_id?: string } & Paged) => http.get<T.Page<T.AuditRow>>('/audit', f as Query).then(asPage),
};

// --- B1 access-identity-team
/** Облікові дані: власник виправляє дані людини / скидає пароль, кожен — змінює свій пароль. */
export const accessApi = {
  patchUser: (userId: string, body: { full_name?: string; email?: string; password?: string }) => http.patch<T.User>(`/users/${userId}`, body),
  changePassword: (current_password: string, new_password: string) => http.post<{ ok: boolean }>('/auth/password', { current_password, new_password }),
  /** Призупинення повертає, скільки відкритих задач і справ ще на людині. */
  setStatus: (userId: string, status: 'active' | 'suspended') =>
    http.patch<T.User & { still_assigned?: { tasks: number; cases: number } }>(`/memberships/${userId}`, { status }),
};

// --- B3 shop-orders-stock
export interface ContactHit {
  id: string;
  full_name: string | null;
  phones: string[];
  emails: string[];
}
export interface LegalEntity {
  id: string;
  name: string;
  kind: 'fop' | 'company' | 'person';
  country: string;
  tax_id: string | null;
  vat_payer: boolean;
  iban: string | null;
  bank: string | null;
  address: string | null;
  is_seller: boolean;
}
export type LegalEntityInput = Partial<Omit<LegalEntity, 'id'>>;
/** Залишок варіанта на одному складі (GET /inventory?variant_id=). */
export type VariantStock = T.InventoryLevel & { location_name?: string };

export const shopApi = {
  /** Пошук клієнта за телефоном/email/імʼям — для підказки «Знайдено клієнта» в новому замовленні. */
  findContacts: (q: string, signal?: AbortSignal) =>
    http.get<T.Page<ContactHit>>('/contacts', { q, limit: 3 }, signal).then(asPage),
  variantStock: (variantId: string, signal?: AbortSignal) =>
    http.get<VariantStock[]>('/inventory', { variant_id: variantId }, signal).then(asArray),
  allLocations: () => http.get<T.StockLocation[]>('/stock-locations', { all: 1 }).then(asArray),
  patchLocation: (id: string, body: { name?: string; is_active?: boolean }) => http.patch<T.StockLocation>(`/stock-locations/${id}`, body),
  legalEntities: () => http.get<LegalEntity[]>('/legal-entities').then(asArray),
  createLegalEntity: (body: LegalEntityInput) => http.post<LegalEntity>('/legal-entities', body),
  patchLegalEntity: (id: string, body: LegalEntityInput) => http.patch<LegalEntity>(`/legal-entities/${id}`, body),
};

/** Завантаження фото з розміром у підписі (сервер відхилить файли понад 10 МБ і SVG). */
export async function uploadProductPhoto(file: File): Promise<string> {
  const mime = file.type || 'image/jpeg';
  const { upload_url, public_url } = await http.post<{ upload_url: string; public_url: string }>('/uploads/image', {
    file_name: file.name,
    mime,
    size: file.size,
  });
  await putFile(upload_url, file, { 'Content-Type': mime });
  return public_url;
}

// --- batch: team-fleet
/** Передача роботи людини колегам і погодження її відсутності одним запитом. */
export const handoverApi = {
  get: (absenceId: string) => http.get<T.HandoverData>(`/absences/${absenceId}/handover`),
  approve: (absenceId: string, body: { note?: string | null; handover?: T.HandoverBody }) =>
    http.post<T.AbsenceHandoverResult>(`/absences/${absenceId}/approve`, body),
};

// --- batch: countries-config
/** Налаштування → Країни: довідник країн і правила документів/дій країни (редагування — pipelineApi.patchRequirement). */
export const countriesApi = {
  list: () => http.get<T.Country[]>('/countries'),
  create: (body: { code: string; name: Record<string, string>; notes?: string | null; partners?: T.CountryPartner[]; is_active?: boolean }) =>
    http.post<T.Country>('/countries', body),
  patch: (code: string, body: { name?: Record<string, string>; notes?: string | null; partners?: T.CountryPartner[]; is_active?: boolean; is_example?: boolean }) =>
    http.patch<T.Country>(`/countries/${code}`, body),
  rules: (code: string) => http.get<T.CountryRules>(`/countries/${code}/requirements`),
  addRule: (code: string, body: T.CountryRuleInput) => http.post<T.CountryRule>(`/countries/${code}/requirements`, body),
  seedExamples: () => http.post<{ countries: number; rules: number }>('/countries/seed-examples', {}),
};

/** Які документи машини обовʼязкові для типу кузова. */
export const vehicleDocRulesApi = {
  list: () => http.get<T.VehicleDocRule[]>('/vehicle-document-rules'),
  put: (key: string, bodyTypes: T.BodyType[]) => http.put<T.VehicleDocRule>(`/vehicle-document-rules/${key}`, { mandatory_body_types: bodyTypes }),
};

// --- batch: pricing-tech
/** Окрема ціна колекції і своя ціна товарів у ній (тонкі обгортки над PATCH /collections/{id} і PUT …/items). */
export const collectionPriceApi = {
  patch: (id: string, body: { price_minor: number | null }) => http.patch<T.Collection & T.CollectionPriceFields>(`/collections/${id}`, body),
  putItems: (id: string, items: { product_id: string; price_minor?: number | null }[]) =>
    http.put<T.Collection & T.CollectionPriceFields>(`/collections/${id}/items`, items),
};

// --- batch: notify-staff
/** Сповіщення (дзвіночок): свої, лічильник непрочитаних, позначити прочитаними. */
export const notificationsApi = {
  list: (f: { unread?: boolean; limit?: number; cursor?: string | null } = {}) =>
    http.get<T.NotificationsPage>('/notifications', { unread: f.unread ? 1 : undefined, limit: f.limit, cursor: f.cursor ?? undefined }),
  unreadCount: () => http.get<{ unread: number }>('/notifications/unread-count'),
  read: (body: { ids?: string[]; all?: boolean }) => http.post<{ updated: number }>('/notifications/read', body),
  runOverdue: () => http.post<{ created: number }>('/notifications/run-overdue'),
};

/** Своє імʼя і телефон. */
export const profileApi = {
  patch: (body: T.ProfileIn) => http.patch<T.ProfileOut>('/me/profile', body),
};

/** «Мої документи»: особисті документи співробітника. */
export const myDocsApi = {
  list: () => http.get<T.MyDocumentsOut>('/documents/mine'),
  uploadUrl: (body: Omit<Parameters<typeof archiveApi.uploadUrl>[0], 'owner_kind'> & { owner_id: string }) =>
    archiveApi.uploadUrl({ ...body, owner_kind: 'employee' }),
  complete: (docId: string) => http.post<T.DocumentRow>(`/documents/mine/${docId}/complete`),
};

/** Свій документ: upload-url → PUT у сховище → complete (як uploadOwnerDocument, але complete доступний будь-якій ролі). */
export async function uploadMyDocument(
  file: File,
  body: Omit<Parameters<typeof myDocsApi.uploadUrl>[0], 'file_name' | 'mime' | 'size'>,
): Promise<string> {
  const mime = file.type || 'application/octet-stream';
  const target = await myDocsApi.uploadUrl({ ...body, file_name: file.name, mime, size: file.size });
  await putFile(target.upload_url, file, target.headers ?? { 'Content-Type': mime }, target.method || 'PUT');
  await myDocsApi.complete(target.document_id);
  return target.document_id;
}

// --- batch: shop-returns-feeds
export const returnsApi = {
  create: (orderId: string, body: T.OrderReturnInput) => http.post<T.OrderReturn>(`/orders/${orderId}/returns`, body),
  list: (orderId: string) => http.get<T.OrderReturn[]>(`/orders/${orderId}/returns`).then(asArray),
  get: (id: string) => http.get<T.OrderReturn>(`/returns/${id}`),
  /** PDF «Акт повернення товару» через fetch (з Bearer) → blob URL, як рахунок замовлення; `sellerMissing` — незаповнені реквізити продавця. */
  documentBlob: async (id: string) => {
    const res = await request<Response>('GET', `/returns/${id}/document.pdf`, { raw: true, headers: { Accept: 'application/pdf' } });
    const missing = res.headers.get('X-Seller-Missing');
    return { url: URL.createObjectURL(await res.blob()), sellerMissing: missing ? missing.split(',') : [] };
  },
};

// --- batch: document-types
/** Типи документів тенанта (Налаштування → Країни): список, створення, зміна, архів. */
export const documentTypesApi = {
  list: () => http.get<T.DocumentTypeFull[]>('/document-types'),
  create: (body: T.DocumentTypeInput) => http.post<T.DocumentTypeFull>('/document-types', body),
  patch: (id: string, body: Partial<T.DocumentTypeInput>) => http.patch<T.DocumentTypeFull>(`/document-types/${id}`, body),
  archive: (id: string) => http.post<T.DocumentTypeFull>(`/document-types/${id}/archive`),
  unarchive: (id: string) => http.post<T.DocumentTypeFull>(`/document-types/${id}/unarchive`),
};

// --- batch: crm-sponsor
/** Партнерські компанії VIA DOMUM (окремі тенанти): список, створення з першим адміністратором, призупинення, скидання пароля. */
export const partnerCompaniesApi = {
  list: () => http.get<T.PartnerCompany[]>('/partner-companies').then(asArray),
  create: (body: T.PartnerCompanyCreate) => http.post<T.PartnerCompany>('/partner-companies', body),
  get: (id: string) => http.get<T.PartnerCompanyDetail>(`/partner-companies/${id}`),
  patch: (id: string, body: { status: T.PartnerCompanyStatus }) => http.patch<T.PartnerCompany>(`/partner-companies/${id}`, body),
  resetPassword: (id: string, userId: string, password: string) =>
    http.post<{ ok: boolean }>(`/partner-companies/${id}/members/${userId}/password`, { password }),
};

/** Передача справи (або лише перевезення) партнеру — сторона того, хто передає. */
export const caseHandoversApi = {
  list: (caseId: string) => http.get<T.Handover[]>(`/cases/${caseId}/handovers`).then(asArray),
  create: (caseId: string, body: T.HandoverCreate) => http.post<T.Handover>(`/cases/${caseId}/handovers`, body),
  patch: (id: string, body: T.HandoverPatch) => http.patch<T.Handover>(`/handovers/${id}`, body),
  revoke: (id: string) => http.post<T.Handover>(`/handovers/${id}/revoke`),
  complete: (id: string) => http.post<T.Handover>(`/handovers/${id}/complete`),
  accessLog: (id: string) => http.get<{ items: T.HandoverAccessRow[] }>(`/handovers/${id}/access-log`).then((r) => r.items ?? []),
  partnerDocUrl: (id: string, docId: string) =>
    http.get<{ url: string; file_name: string; mime: string }>(`/handovers/${id}/partner-documents/${docId}/download-url`),
};

// --- batch: crm-partner
/** «Передані нам»: справи, які інша компанія передала нашому тенанту (лише дозволений відправником зріз). */
export const receivedHandoversApi = {
  list: () => http.get<T.ReceivedHandover[] | T.Page<T.ReceivedHandover>>('/handovers/received').then(asArray),
  get: (id: string) => http.get<T.ReceivedHandoverDetail>(`/handovers/received/${id}`),
  accept: (id: string) => http.post<T.ReceivedHandoverDetail>(`/handovers/received/${id}/accept`),
  decline: (id: string) => http.post<T.ReceivedHandoverDetail>(`/handovers/received/${id}/decline`),
  progress: (id: string, body: T.HandoverProgressIn) => http.put<T.ReceivedHandoverDetail>(`/handovers/received/${id}/progress`, body),
  docUrl: (id: string, docId: string) =>
    http.get<{ url: string; file_name: string; mime: string }>(`/handovers/received/${id}/documents/${docId}/download-url`),
  uploadUrl: (id: string, body: { document_type_key: string; file_name: string; mime: string; size: number }) =>
    http.post<T.UploadUrlOut>(`/handovers/received/${id}/documents/upload-url`, body),
  /** Привʼязати свій рейс до прийнятої передачі (null — відвʼязати). */
  linkTrip: (tripId: string, handoverId: string | null) => http.patch<T.Trip>(`/trips/${tripId}`, { handover_id: handoverId }),
};

// --- batch: ws-core
/** Робочі простори: мої простори, головна простору, огляд власника, допуски людей (owner). */
export const workspacesApi = {
  list: () => http.get<{ current: T.WorkspaceKey | null; items: T.WorkspaceInfo[] }>('/workspaces'),
  home: (key: string) => http.get<T.WorkspaceHome>(`/workspaces/${key}/home`),
  overview: () => http.get<{ as_of: string; tiles: T.OverviewTile[] }>('/overview'),
  members: (key: string) => http.get<T.WorkspaceMember[]>(`/workspaces/${key}/members`),
  putMember: (key: string, userId: string, body: { is_director: boolean }) =>
    http.put<T.WorkspaceMember>(`/workspaces/${key}/members/${userId}`, body),
  removeMember: (key: string, userId: string) => http.del<unknown>(`/workspaces/${key}/members/${userId}`),
};

/** Транспортний документ до переданої справи: upload-url → PUT у сховище → complete (як uploadMyDocument). */
export async function uploadHandoverDocument(id: string, file: File, document_type_key: string): Promise<string> {
  const mime = file.type || 'application/octet-stream';
  const target = await receivedHandoversApi.uploadUrl(id, { document_type_key, file_name: file.name, mime, size: file.size });
  await putFile(target.upload_url, file, target.headers ?? { 'Content-Type': mime }, target.method || 'PUT');
  await documentsApi.complete(target.document_id);
  return target.document_id;
}

// --- batch: C-ritual
/** Ритуальні послуги в Україні: можливості допродажу після репатріації (етапи, рядки, воронка). */
export const ritualApi = {
  list: (f: { stage?: string; assigned_to?: string; q?: string; case_id?: string; open?: 1 } & Paged = {}) =>
    http.get<T.Page<T.RitualOpportunity>>('/ritual/opportunities', f as Query).then(asPage),
  get: (id: string) => http.get<T.RitualOpportunityDetail>(`/ritual/opportunities/${id}`),
  create: (body: { case_id: string; assignee_id?: string | null; note?: string | null }) =>
    http.post<T.RitualOpportunityDetail>('/ritual/opportunities', body),
  patch: (
    id: string,
    body: Partial<{ assignee_id: string | null; note: string | null; contact_id: string | null; value_minor: number; currency: string }>,
  ) => http.patch<T.RitualOpportunityDetail>(`/ritual/opportunities/${id}`, body),
  stage: (id: string, body: { to: T.RitualStage; note?: string | null; decline_reason?: string | null }) =>
    http.post<T.RitualOpportunityDetail>(`/ritual/opportunities/${id}/stage`, body),
  putItems: (id: string, items: Omit<T.RitualItem, 'id' | 'total_minor' | 'position'>[]) =>
    http.put<T.RitualOpportunityDetail>(`/ritual/opportunities/${id}/items`, items),
  funnel: (f: { from?: string; to?: string } = {}) => http.get<T.RitualFunnel>('/ritual/funnel', f as Query),
};

// --- batch: D-quality
/** Якість і зворотний звʼязок: скарги, відгуки, проблеми й ідеї (035). */
export const qualityApi = {
  list: (f: T.QualityFilter & Paged = {}) => http.get<T.Page<T.QualityItem>>('/quality/items', f as Query).then(asPage),
  get: (id: string) => http.get<T.QualityItemDetail>(`/quality/items/${id}`),
  create: (body: T.QualityCreate) => http.post<T.QualityItemDetail>('/quality/items', body),
  patch: (id: string, body: T.QualityPatch) => http.patch<T.QualityItemDetail>(`/quality/items/${id}`, body),
  comment: (id: string, body: string) => http.post<T.QualityItemDetail>(`/quality/items/${id}/comments`, { body }),
  summary: () => http.get<T.QualitySummary>('/quality/summary'),
};

/** Прийом «проблеми / ідеї» від партнерської компанії (кнопка в кабінеті партнера — фаза 3). Читання назад немає. */
export const partnerQualityApi = {
  submit: (body: { kind: 'problem' | 'idea'; title: string; body?: string; handover_id?: string }) =>
    http.post<{ id: string }>('/partner/quality-items', body),
};

// --- batch: F-money
/** Гроші (037): платежі, рахунки, місячні підсумки. */
export const moneyApi = {
  payments: (f: T.PaymentFilter & Paged = {}) => http.get<T.Page<T.Payment>>('/money/payments', f as Query).then(asPage),
  createPayment: (body: T.PaymentCreate) => http.post<T.Payment>('/money/payments', body),
  patchPayment: (id: string, body: { note?: string | null; method?: T.PaymentMethod; paid_at?: string }) =>
    http.patch<T.Payment>(`/money/payments/${id}`, body),
  removePayment: (id: string) => http.del<{ ok: boolean }>(`/money/payments/${id}`),
  invoices: (f: T.InvoiceFilter & Paged = {}) => http.get<T.Page<T.Invoice>>('/money/invoices', f as Query).then(asPage),
  invoice: (id: string) => http.get<T.InvoiceDetail>(`/money/invoices/${id}`),
  createInvoice: (body: T.InvoiceCreate) => http.post<T.Invoice>('/money/invoices', body),
  issue: (id: string) => http.post<T.Invoice>(`/money/invoices/${id}/issue`),
  cancelInvoice: (id: string) => http.post<T.Invoice>(`/money/invoices/${id}/cancel`),
  summary: (month?: string) => http.get<T.MoneySummary>('/money/summary', { month }),
};

// --- batch: E-marketing
/** Маркетинг (036): конверсія за джерелами/сайтами/UTM, коротке зведення, сайти. */
export const marketingApi = {
  conversion: (f: { group?: T.ConversionGroup; from?: string; to?: string } = {}) =>
    http.get<T.ConversionReport>('/marketing/conversion', f as Query),
  summary: () => http.get<T.MarketingSummary>('/marketing/summary'),
  sites: () => http.get<{ from: string; to: string; items: T.MarketingSite[] }>('/marketing/sites'),
};
/** Додатково до leadsApi: маркетингові поля заявки (owner/manager) і фільтри списку. */
export const leadsMarketingApi = {
  /** PATCH /leads/{id} з джерелом/каналом/UTM/поведінкою. */
  behaviour: (id: string, body: T.LeadMarketingPatch) => http.patch<T.Lead>(`/leads/${id}`, body),
  list: (f: T.LeadFilter & { source?: string; is_bot?: 0 | 1; utm_source?: string } & Paged) =>
    http
      .get<T.Page<T.Lead> & T.CountryHidden>('/leads', f as Query)
      .then((r) => ({ ...asPage(r), hidden_by_country: r?.hidden_by_country ?? 0, country_filter: r?.country_filter ?? null })),
};

// batch: p4-D
/** Валюта справи (050): вручну → країна призначення → сайт → UAH; null прибирає ручний вибір. */
export const caseCurrencyApi = {
  get: (caseId: string) => http.get<T.CaseCurrency>(`/cases/${caseId}/currency`),
  set: (caseId: string, currency: string | null) => http.post<T.CaseCurrency>(`/cases/${caseId}/currency`, { currency }),
};
/** Типова валюта справ країни призначення (050) — для вкладки «Типово для справ». */
export const countryCurrencyApi = {
  get: (code: string) => http.get<T.CountryCurrency>(`/countries/${code}/currency`),
  put: (code: string, caseCurrency: string | null) =>
    http.put<T.CountryCurrency>(`/countries/${code}/currency`, { case_currency: caseCurrency }),
};

// batch: p4-A
/** «Чого чекаємо і від кого» (047): відкриті документи всіх справ, лічильник для меню, нагадування за терміном. */
export const waitingApi = {
  list: (f: T.WaitingFilter = {}) => http.get<T.WaitingList>('/waiting', f as Query),
  summary: () => http.get<{ total: number; overdue: number }>('/waiting/summary'),
  runReminders: () => http.post<{ count: number }>('/waiting/run-reminders'),
};
/** Пункт чекліста (047): від кого і до коли чекаємо, нагадати, «отримано без файлу». */
export const checklistApi = {
  patchParty: (itemId: string, body: T.ChecklistPartyPatch) => http.patch<T.ChecklistItem>(`/checklist/${itemId}`, body),
  remind: (itemId: string) => http.post<T.RemindResult>(`/checklist/${itemId}/remind`),
  received: (itemId: string, note?: string) => http.post<T.ChecklistItem>(`/checklist/${itemId}/received`, { note: note || undefined }),
};

// batch: p4-B
/** Контакти й інструкції країн, поради з досвіду, «Що робити?» у справі, винятки з правила (048). */
export const guidesApi = {
  contacts: (code: string) => http.get<T.CountryContact[]>(`/countries/${code}/contacts`),
  addContact: (code: string, body: T.CountryContactInput) => http.post<T.CountryContact>(`/countries/${code}/contacts`, body),
  patchContact: (id: string, body: T.CountryContactInput) => http.patch<T.CountryContact>(`/country-contacts/${id}`, body),
  deleteContact: (id: string) => http.del<{ ok: boolean }>(`/country-contacts/${id}`),
  guides: (code: string) => http.get<T.CountryGuide[]>(`/countries/${code}/guides`),
  putGuide: (code: string, body: T.GuideInput) => http.put<T.CountryGuide>(`/countries/${code}/guides`, body),
  deleteGuide: (id: string) => http.del<{ ok: boolean }>(`/guides/${id}`),
  addTip: (guideId: string, body: { body: string; case_id?: string }) => http.post<T.GuideTip>(`/guides/${guideId}/tips`, body),
  deleteTip: (id: string) => http.del<{ ok: boolean }>(`/tips/${id}`),
  caseTip: (caseId: string, body: T.CaseTipInput) =>
    http.post<{ guide: T.CountryGuide; tip: T.GuideTip }>(`/cases/${caseId}/tips`, body),
  help: (caseId: string, sel: T.HelpSelector) => http.get<T.CaseHelp>(`/cases/${caseId}/help`, { [sel.kind]: sel.key }),
  exceptions: (caseId: string) => http.get<T.RuleException[]>(`/cases/${caseId}/rule-exceptions`),
  addException: (caseId: string, body: T.RuleExceptionInput) =>
    http.post<{ exception: T.RuleException; item?: unknown; task?: unknown }>(`/cases/${caseId}/rule-exceptions`, body),
};
/** «Типово для справ» країни: контракти батчів C (/defaults) і D (/currency). Поки їх немає — 404. */
export const countryDefaultsApi = {
  get: (code: string) => http.get<T.CountryDefaults>(`/countries/${code}/defaults`),
  put: (code: string, body: { default_manager_id?: string | null; manager_match?: T.CountryDefaults['manager_match'] }) =>
    http.put<T.CountryDefaults>(`/countries/${code}/defaults`, body),
  getCurrency: (code: string) => http.get<T.CountryCurrency>(`/countries/${code}/currency`),
  putCurrency: (code: string, caseCurrency: string | null) =>
    http.put<T.CountryCurrency>(`/countries/${code}/currency`, { case_currency: caseCurrency }),
};

// --- batch: p5-F
/**
 * Вкладка «Родина» у справі: кроки для родини (батч A), повідомлення родині й бажаний канал (батч B),
 * запит на відгук (батч D). Поки бекенд не злитий — 404, вкладка показує «Ще не підключено».
 */
export const familyApi = {
  steps: (caseId: string) => http.get<T.FamilyStepsOut>(`/cases/${caseId}/family-steps`),
  setStep: (caseId: string, key: T.FamilyStepKey, body: T.FamilyStepInput) =>
    http.post<T.FamilyStep>(`/cases/${caseId}/family-steps/${key}`, body),
  clientNotifications: (caseId: string) =>
    http.get<{ items: T.ClientNotification[] }>(`/cases/${caseId}/client-notifications`).then((r) => r?.items ?? []),
  /** null — «авто» (канал вибирає система). */
  setPreferredChannel: (contactId: string, value: T.PreferredChannel | null) =>
    http.patch<{ id: string; preferred_channel: T.PreferredChannel | null }>(`/contacts/${contactId}`, { preferred_channel: value }),
  feedback: (caseId: string) => http.get<T.FeedbackInfo>(`/cases/${caseId}/feedback`),
  setFeedback: (caseId: string, body: T.FeedbackPatch) => http.patch<T.FeedbackInfo>(`/cases/${caseId}/feedback`, body),
};

// --- batch: p5-E
/** Імпорт справ і лідів (дані старого бота) зі шаблону CSV/XLSX: owner і manager. Файл — JSON з base64. */
export const importsApi = {
  /** Шаблон як файл (blob) — завантажуємо через fetch з Bearer, як PDF. */
  template: async (kind: T.ImportKind, format: 'csv' | 'xlsx') => {
    const res = await request<Response>('GET', '/imports/template', { raw: true, query: { kind, format } });
    return res.blob();
  },
  preview: (body: T.ImportPreviewIn) => http.post<T.ImportPreview>('/imports/preview', body),
  run: (jobId: string, dryRun: boolean) => http.post<T.ImportRunOut>(`/imports/${jobId}/run`, { dry_run: dryRun }),
  list: () => http.get<{ items: T.ImportJob[] }>('/imports').then((r) => r?.items ?? []),
  get: (jobId: string) => http.get<T.ImportJob & { columns: T.ImportColumn[]; rows: T.ImportRow[] }>(`/imports/${jobId}`),
};

/** Сигнали парсера груп (063): пост із групи, ще не заявка. «Взяти» — твій; «Заявка» — лід tg_group. */
export interface Signal {
  id: string;
  text: string;
  group_name: string | null;
  group_link: string | null;
  msg_link: string | null;
  lang: string | null;
  score: number | null;
  posted_at: string | null;
  created_at: string;
  status: 'new' | 'taken' | 'not_ours';
  taken_by: string | null;
  taken_by_name: string | null;
  lead_id: string | null;
  lead_number: string | null;
}
export const signalsApi = {
  list: (status: Signal['status'] = 'new') => http.get<{ items: Signal[] }>('/signals', { status, limit: 200 }),
  take: (id: string) => http.post<{ ok: boolean }>(`/signals/${id}/take`),
  dismiss: (id: string) => http.post<{ ok: boolean }>(`/signals/${id}/dismiss`),
  lead: (id: string, body: { name?: string; phone?: string; email?: string }) =>
    http.post<{ lead_id: string; number: string | null; repeat: boolean }>(`/signals/${id}/lead`, body),
};

/** Екран «Парсер» (065): знімок парсера — акаунти, групи, дублі, куди додати нові групи. */
export interface ParserAccount {
  id: number;
  label: string | null;
  phone: string;
  status: string;
  has_proxy: boolean;
  logged_in: boolean;
  usable: boolean;
  reads: number;
  queued: number;
  member_of: number;
  msgs_per_day: number;
  flood_count: number;
  flood_until: string | null;
}
export interface ParserGroup {
  id: number;
  username: string | null;
  title: string | null;
  country: string | null;
  reader: number | null;
  join_status: string;
  members_count: number | null;
  msgs_per_day: number | null;
  accounts: number[];
  duplicate: boolean;
  extra: number[];
}
export interface ParserOverview {
  received_at: string | null;
  stale: boolean;
  limits: { max_groups_per_account?: number; joins_per_day?: number };
  accounts: ParserAccount[];
  totals: { accounts: number; usable: number; groups: number; joined: number; queued: number; duplicates: number;
    no_proxy: number; load_min: number; load_max: number };
  duplicates: ParserGroup[];
}
export interface ParserSuggestion {
  add: { link: string; account: number }[];
  existing: { link: string; title: string | null; reader: number | null; accounts: number[]; join_status: string }[];
  bad: string[];
  full: string[];
  load_after: Record<string, number>;
  days_to_join: number;
  command: string | null;
}
export const parserApi = {
  overview: () => http.get<ParserOverview>('/parser/overview'),
  groups: (q: { q?: string; account?: number; duplicate?: boolean; status?: string; limit?: number }) =>
    http.get<{ items: ParserGroup[]; total: number }>('/parser/groups', q as Query),
  suggest: (links: string[], accounts?: number[]) => http.post<ParserSuggestion>('/parser/suggest', { links, accounts }),
};

/** Звіти про баги й побажання щодо CRM (066): кнопка «Повідомити про проблему» у верхній панелі, сторінка /reports. */
export type ReportKind = 'bug' | 'idea' | 'question';
export type ReportStatus = 'new' | 'in_progress' | 'fixed' | 'wontfix' | 'duplicate';
export type ReportPriority = 'low' | 'normal' | 'high' | 'critical';
export interface ProductReportIn {
  kind: ReportKind;
  title?: string;
  body: string;
  app?: 'crm';
  page_url?: string;
  user_agent?: string;
  viewport?: string;
  app_version?: string;
  client_errors?: string[];
  /** data:image/png;base64,… — до 3 шт., кожен до 5 МБ */
  screenshots?: string[];
}
export interface ProductReport {
  id: string;
  number: string | null;
  kind: ReportKind;
  title: string | null;
  body: string;
  page_url: string | null;
  user_agent: string | null;
  viewport: string | null;
  app_version: string | null;
  client_errors: string[];
  status: ReportStatus;
  priority: ReportPriority;
  created_by: string | null;
  created_by_name: string | null;
  assigned_to: string | null;
  assigned_to_name: string | null;
  resolution: string | null;
  resolved_at: string | null;
  created_at: string;
}
export type ProductReportRow = ProductReport & { screenshots: number };
export type ProductReportFull = ProductReport & { screenshots: { key: string; url: string; size: number }[] };
export const reportsApi = {
  create: (body: ProductReportIn) => http.post<{ id: string; number: string | null }>('/product-reports', body),
  list: (q: { status?: ReportStatus; kind?: ReportKind; open?: boolean; mine?: boolean; q?: string } = {}) =>
    http.get<{ items: ProductReportRow[]; counts: { new: number; in_progress: number; total: number } }>('/product-reports', {
      ...q,
      limit: 300,
    }),
  get: (id: string) => http.get<ProductReportFull>(`/product-reports/${id}`),
  patch: (id: string, body: { status?: ReportStatus; priority?: ReportPriority; resolution?: string }) =>
    http.patch<ProductReportFull>(`/product-reports/${id}`, body),
  exportMd: (status = 'new,in_progress') => http.get<{ markdown: string; count: number }>('/product-reports-export', { status }),
};

/** Корзина заявок (067): видалення з причиною, 30 днів лише відновлення, потім — стерти вручну (власник). */
export interface TrashedLead {
  id: string;
  number: string | null;
  message: string | null;
  source: string;
  status: string;
  created_at: string;
  deleted_at: string;
  delete_reason: string | null;
  deleted_by_name: string | null;
  contact_name: string | null;
  phone: string | null;
  purge_after: string;
}
export const leadTrashApi = {
  remove: (id: string, reason: string) => http.post<{ ok: boolean }>(`/leads/${id}/delete`, { reason }),
  list: () => http.get<{ items: TrashedLead[]; days: number }>('/leads-trash'),
  restore: (id: string) => http.post<unknown>(`/leads/${id}/restore`),
  purge: (id: string) => http.del<{ ok: boolean }>(`/leads/${id}`),
};
